import { apiClient } from 'shared/lib/apiClient';

export interface LocalizedText {
  ar: string;
  he: string;
  en: string;
}

export interface TrainingVideoView {
  id: string;
  titleKey: string;
  url: string;
  completed: boolean;
}

export interface SanitizedQuizQuestion {
  id: string;
  prompt: LocalizedText;
  options: LocalizedText[];
}

export interface StageQuizView {
  stageId: string;
  questions: SanitizedQuizQuestion[];
  savedAnswers: Record<string, number>;
  answered: boolean;
}

export interface TrainingStageView {
  id: string;
  order: number;
  titleKey: string;
  videos: TrainingVideoView[];
  hasQuiz: boolean;
  videosWatched: boolean;
  unlocked: boolean;
  done: boolean;
  quiz: StageQuizView | null;
}

export type QuizStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'RETRY_AVAILABLE'
  | 'FINISHED';

export interface QuizStateView {
  attemptNumber: number;
  attemptsUsed: number;
  maxAttempts: number;
  status: QuizStatus;
  finished: boolean;
  canRetry: boolean;
  passThreshold: number;
  totalQuestions: number;
}

export interface TrainingView {
  stages: TrainingStageView[];
  quiz: QuizStateView;
  completedCount: number;
  totalCount: number;
  isComplete: boolean;
}

export const fetchTraining = async (): Promise<TrainingView> => {
  const { data } = await apiClient.get<TrainingView>('/api/courier/me/training');
  return data;
};

export const markVideoComplete = async (
  videoId: string,
): Promise<TrainingView> => {
  const { data } = await apiClient.post<TrainingView>(
    `/api/courier/me/training/video/${encodeURIComponent(videoId)}/complete`,
  );
  return data;
};

export const startQuizAttempt = async (): Promise<TrainingView> => {
  const { data } = await apiClient.post<TrainingView>(
    '/api/courier/me/training/attempt/start',
  );
  return data;
};

export const submitStageAnswers = async (
  stageId: string,
  answers: Record<string, number>,
): Promise<TrainingView> => {
  const { data } = await apiClient.post<TrainingView>(
    `/api/courier/me/training/stage/${encodeURIComponent(stageId)}/answers`,
    { answers },
  );
  return data;
};

export const finalizeQuizAttempt = async (): Promise<TrainingView> => {
  const { data } = await apiClient.post<TrainingView>(
    '/api/courier/me/training/attempt/finalize',
  );
  return data;
};
